import { useState, useEffect } from 'react';
import * as styles from '../styles/components/dataset-reload-modal.css.ts';
import logger from '../utils/logger';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';

interface DatasetReloadModalProps {
  datasetName: string;
  originalFileName?: string;
  sourceUrl?: string;  // URLから読み込んだデータセットの場合
  onClose: () => void;
  onReload: (source: string | File) => Promise<void>;  // stringはURL、Fileはローカルファイル
}

export function DatasetReloadModal({
  datasetName,
  originalFileName,
  sourceUrl,
  onClose,
  onReload
}: DatasetReloadModalProps) {
  const [isReloading, setIsReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // モーダルが開いたときにエラーをクリア
  useEffect(() => {
    setError(null);
  }, [originalFileName, sourceUrl]);

  const handleReloadFromUrl = async () => {
    if (!sourceUrl) return;

    try {
      setIsReloading(true);
      setError(null);
      logger.info('[DatasetReloadModal] Reloading from URL:', sourceUrl);
      await onReload(sourceUrl);
      onClose();
    } catch (err) {
      logger.error('[DatasetReloadModal] Failed to reload from URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to reload dataset');
    } finally {
      setIsReloading(false);
    }
  };

  const handleReloadWithFile = async () => {
    try {
      setIsReloading(true);
      setError(null);

      // ファイル選択ダイアログを表示
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv,.tsv';

      const file = await new Promise<File>((resolve, reject) => {
        input.onchange = (e) => {
          const files = (e.target as HTMLInputElement).files;
          if (files && files.length > 0) {
            resolve(files[0]);
          } else {
            reject(new Error('No file selected'));
          }
        };
        input.click();
      });

      await onReload(file);
      onClose();
    } catch (err) {
      logger.error('[DatasetReloadModal] Failed to reload dataset:', err);
      setError(err instanceof Error ? err.message : 'Failed to reload dataset');
    } finally {
      setIsReloading(false);
    }
  };

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title="Reload Dataset"
      width="lg"
      closeOnOverlayClick={!isReloading}
      showCloseButton={!isReloading}
      footer={
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isReloading}
        >
          Cancel
        </Button>
      }
    >
      <div>
        <div className={styles.datasetInfo}>
          <span className={styles.label}>Current dataset:</span>
          <span className={styles.datasetName}>{datasetName}</span>
          {sourceUrl ? (
            <>
              <br />
              <span className={styles.label}>Source URL:</span>
              <span className={styles.filePath}>{sourceUrl}</span>
            </>
          ) : originalFileName && (
            <>
              <br />
              <span className={styles.label}>Original file:</span>
              <span className={styles.filePath}>{originalFileName}</span>
            </>
          )}
        </div>

        <div className={styles.description}>
          <p>
            Reload the dataset with new data. The file must have the same structure as the original.
          </p>
        </div>

        <div className={styles.requirements}>
          <h3>Requirements</h3>
          <ul>
            <li>Same number of columns</li>
            <li>Same column order</li>
            <li>Same data types (numeric, text, date, etc.)</li>
          </ul>
        </div>

        <div className={styles.preservedInfo}>
          <h3>What will be preserved</h3>
          <ul>
            <li>Column names (as customized in MIDAS)</li>
            <li>Measurement scales</li>
            <li>Derived datasets and analysis</li>
          </ul>
        </div>

        <div className={styles.warning}>
          <div className={styles.warningIcon}>⚠️</div>
          <div className={styles.warningText}>
            <strong>Warning:</strong> All excluded rows and row comments will be cleared.
          </div>
        </div>

        <div className={styles.fileSelectSection}>
          <h3>Choose reload method</h3>

          {/* URLソースの場合: Reload from URLオプション */}
          {sourceUrl && (
            <div className={styles.reloadOption}>
              <button
                className={styles.optionButton}
                onClick={handleReloadFromUrl}
                disabled={isReloading}
                data-testid="reload-from-url-button"
              >
                {isReloading ? (
                  <>
                    <span className={styles.spinner} />
                    Reloading...
                  </>
                ) : (
                  <>
                    Reload from URL
                  </>
                )}
              </button>
              <p className={styles.optionDescription}>
                Fetch the latest data from the original URL
              </p>
            </div>
          )}

          {/* ファイル選択オプション */}
          <div className={styles.reloadOption}>
            <button
              className={styles.optionButton}
              onClick={handleReloadWithFile}
              disabled={isReloading}
              data-testid="reload-from-file-button"
            >
              {isReloading ? (
                <>
                  <span className={styles.spinner} />
                  Reloading...
                </>
              ) : (
                <>
                  Select file to reload
                </>
              )}
            </button>
            <p className={styles.optionDescription}>
              Choose a CSV file with the same column structure
            </p>
          </div>
        </div>

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
}